import React, { useState, useMemo, useEffect } from 'react';
import { observer } from 'mobx-react';
import { Table } from '../table/table';
import { TreuebonusPlanungTableModel, TreuebonusAgentur } from './TreuebonusPlanung.table.model';
import { Icon } from '../common/Icon';
import { InputControl } from '../common/InputControl';
import { Button } from '../common/Button';
import { Modal } from '../Modal';
import { formatNumTwoDig } from 'app/utils';
import { AgenturListsFilter } from '../agt/AgenturListFilter';

interface ITreuebonusPlanungProps {
	vbl?: {
		name: string;
		vd: string;
		gs: string;
		pool: number;
		alreadyAllocated: number;
	};
	agenturen?: TreuebonusAgentur[];
}

// Sample data for development/demo
const SAMPLE_VBL = {
	name: 'S. Berger',
	vd: 'VD München-Süd',
	gs: 'GS München',
	pool: 145000,
	alreadyAllocated: 47820,
};

const SAMPLE_AGENTUREN: TreuebonusAgentur[] = [
	{
		agtId: '47119024',
		bnr: '4711902',
		vd: 'VD M-Süd',
		name: 'Müller & Partner OHG',
		used: 8200,
		active: 4800,
		lastYear: 6000,
		lastRequest: { date: '14.02.2026', amount: 6000, status: 'genehmigt' },
		historyCount: 3,
		allocation: 0,
	},
	{
		agtId: '47118856',
		bnr: '4711885',
		vd: 'VD M-Süd',
		name: 'Allianz Generalvertretung Berger',
		used: 3100,
		active: 1900,
		lastYear: 2500,
		lastRequest: { date: '21.01.2026', amount: 2500, status: 'genehmigt' },
		historyCount: 1,
		allocation: 0,
	},
	{
		agtId: '47120113',
		bnr: '4712011',
		vd: 'VD M-Ost',
		name: 'Schneider Versicherungen e.K.',
		used: 14200,
		active: 9800,
		lastYear: 12000,
		lastRequest: { date: '04.03.2026', amount: 12000, status: 'genehmigt' },
		historyCount: 2,
		allocation: 0,
	},
	{
		agtId: '47119987',
		bnr: '4711998',
		vd: 'VD M-Süd',
		name: 'Hofmann Agentur',
		used: 450,
		active: 550,
		lastYear: 1000,
		lastRequest: { date: '08.04.2026', amount: 1000, status: 'in_pruefung' },
		historyCount: 1,
		allocation: 0,
	},
	{
		agtId: '47118402',
		bnr: '4711840',
		vd: 'VD M-Nord',
		name: 'Weber & Söhne GbR',
		used: 4900,
		active: 3100,
		lastYear: 4000,
		lastRequest: { date: '12.02.2026', amount: 4000, status: 'genehmigt' },
		historyCount: 1,
		allocation: 0,
	},
	{
		agtId: '47120441',
		bnr: '4712044',
		vd: 'VD M-Ost',
		name: 'Becker Vertretung',
		used: 11300,
		active: 6700,
		lastYear: 9500,
		lastRequest: { date: '02.03.2026', amount: 9500, status: 'genehmigt' },
		historyCount: 2,
		allocation: 0,
	},
	{
		agtId: '47119215',
		bnr: '4711921',
		vd: 'VD M-Süd',
		name: 'Klein Agentur München-Süd',
		used: 2100,
		active: 1900,
		lastYear: 2000,
		lastRequest: { date: '17.01.2026', amount: 2000, status: 'genehmigt' },
		historyCount: 1,
		allocation: 0,
	},
	{
		agtId: '47118770',
		bnr: '4711877',
		vd: 'VD M-Nord',
		name: 'Wagner & Co. KG',
		used: 18450,
		active: 11550,
		lastYear: 15000,
		lastRequest: { date: '28.02.2026', amount: 15000, status: 'genehmigt' },
		historyCount: 2,
		allocation: 0,
	},
	{
		agtId: '47120098',
		bnr: '4712009',
		vd: 'VD M-Ost',
		name: 'Fischer Versicherungsbüro',
		used: 3950,
		active: 2050,
		lastYear: 3000,
		lastRequest: { date: '11.02.2026', amount: 3000, status: 'genehmigt' },
		historyCount: 1,
		allocation: 0,
	},
	{
		agtId: '47119553',
		bnr: '4711955',
		vd: 'VD M-Süd',
		name: 'Schmidt Generalagentur',
		used: 7800,
		active: 4200,
		lastYear: 6500,
		lastRequest: { date: '23.01.2026', amount: 6500, status: 'genehmigt' },
		historyCount: 1,
		allocation: 0,
	},
	{
		agtId: '47120556',
		bnr: '4712055',
		vd: 'VD M-Nord',
		name: 'Krause & Bauer KG',
		used: 2400,
		active: 1100,
		lastYear: 2000,
		lastRequest: { date: '06.02.2026', amount: 2000, status: 'genehmigt' },
		historyCount: 1,
		allocation: 0,
	},
	{
		agtId: '47119880',
		bnr: '4711988',
		vd: 'VD M-Ost',
		name: 'Neumann Versicherungsmakler',
		used: 6800,
		active: 3200,
		lastYear: 5500,
		lastRequest: { date: '19.02.2026', amount: 5500, status: 'in_pruefung' },
		historyCount: 2,
		allocation: 0,
	},
];

export const TreuebonusPlanung: React.FC<ITreuebonusPlanungProps> = observer((props) => {
	const vbl = props.vbl || SAMPLE_VBL;
	const seedData = props.agenturen || SAMPLE_AGENTUREN;

	const [allocations, setAllocations] = useState<Record<string, number>>(() => Object.fromEntries(seedData.map((a) => [a.agtId, 0])));
	const [query, setQuery] = useState('');
	const [checkoutOpen, setCheckoutOpen] = useState(false);
	const [successMessage, setSuccessMessage] = useState<string | null>(null);
	const [historyDrawerAgtId, setHistoryDrawerAgtId] = useState<string | null>(null);

	const tm = useMemo(() => {
		const tableModel = new TreuebonusPlanungTableModel();
		tableModel.onAllocationChange = (agtId: string, value: number) => {
			setAllocations((prev) => {
				const cur = prev[agtId] || 0;
				const delta = value - cur;
				const newTotal = distributed + delta;
				let clamped = value;
				if (newTotal > availablePool) {
					clamped = cur + (availablePool - distributed);
				}
				return { ...prev, [agtId]: clamped };
			});
		};
		tableModel.onShowHistory = (agtId: string) => {
			setHistoryDrawerAgtId(agtId);
		};
		return tableModel;
	}, []);

	const distributed = Object.values(allocations).reduce((s, v) => s + v, 0);
	const availablePool = vbl.pool - vbl.alreadyAllocated;
	const remainingPool = availablePool - distributed;

	// Update table model's remainingPool for input clamping
	tm.remainingPool = remainingPool;

	// Load and filter data into table model
	useEffect(() => {
		let list = seedData.map((agt) => ({
			...agt,
			allocation: allocations[agt.agtId] || 0,
		}));

		// Filter
		if (query.trim()) {
			const q = query.trim().toLowerCase();
			list = list.filter((a) => a.agtId.toLowerCase().includes(q) || a.bnr.toLowerCase().includes(q) || a.name.toLowerCase().includes(q) || a.vd.toLowerCase().includes(q));
		}

		tm.setRowData(list);
		tm.sort();
	}, [seedData, query, tm, allocations]);

	const distribEvenly = () => {
		const remaining = availablePool;
		const perAgt = Math.floor(remaining / seedData.length / 100) * 100;
		const next = Object.fromEntries(seedData.map((a) => [a.agtId, perAgt]));
		setAllocations(next);
	};

	const matchLastYear = () => {
		let pool = availablePool;
		const next: Record<string, number> = {};
		for (const a of seedData) {
			const want = a.lastYear;
			const give = Math.min(want, Math.max(0, pool));
			next[a.agtId] = give;
			pool -= give;
		}
		setAllocations(next);
	};

	const resetAll = () => {
		setAllocations(Object.fromEntries(seedData.map((a) => [a.agtId, 0])));
	};

	const submit = () => {
		setCheckoutOpen(false);
		const items = seedData.filter((a) => allocations[a.agtId] !== 0);
		const ref = 'TB-' + Math.floor(100000 + Math.random() * 900000);
		setSuccessMessage(`Antrag erfolgreich übermittelt: ${items.length} Agenturen · ${fmtEURsigned(distributed)} · Vorgangs-Nr. ${ref}`);
		setAllocations(Object.fromEntries(seedData.map((a) => [a.agtId, 0])));
		setTimeout(() => setSuccessMessage(null), 8000);
	};

	const checkoutItems = seedData
		.map((a) => ({
			agtId: a.agtId,
			bnr: a.bnr,
			name: a.name,
			delta: allocations[a.agtId] || 0,
		}))
		.filter((i) => i.delta !== 0);

	const usedPct = Math.max(0, Math.min(100, (distributed / availablePool) * 100));

	const fmtEUR = (n: number) => formatNumTwoDig(n) + ' €';
	const fmtEURsigned = (n: number) => (n > 0 ? '+' : '') + fmtEUR(n);

	return (
		<div className="">
			{/* Hero / pool overview */}
			<div className="grid gap-3 is-col-bigger-right pad-1rem ">
				<div className="page-header content">
					<h2>Treuebonus-Planung</h2>
					<small className="color-grey">Verteilen Treuebonus-Budget auf Ihre Agenturen. Lorem ipsum dolor sit amet consectetur adipisicing elit.</small>
				</div>

				{/* Stats */}
				<div className="grid is-col-auto align-self-end">
					<div className="grid is-col-auto gap-3">
						<div className="">
							<strong className="color-grey">Jahresbudget</strong>
							<div className="title is-4">{fmtEUR(vbl.pool)}</div>
						</div>
						<div className="">
							<strong className="color-grey">Bereits verteilt</strong>
							<div className="title is-4">{fmtEUR(vbl.alreadyAllocated)}</div>
						</div>
						<div className="">
							<strong className="color-grey">Verfügbar</strong>
							<div className="title is-4 color-primary">{fmtEUR(availablePool)}</div>
						</div>
					</div>
				</div>
			</div>

			{/* Planning summary */}
			<div className="pad-1rem">
				<div className="note-box no-grid is-turquoise">
					<div className="grid is-col-auto gap-3 align-center">
						<div>
							<strong className="color-grey">Diese Planung</strong>
							<div className={`title is-4 ${distributed > 0 ? 'color-green' : ''}`}>{fmtEURsigned(distributed)}</div>
							<small className="color-grey">{Object.values(allocations).filter((v) => v !== 0).length} Agenturen</small>
						</div>
						<div>
							<strong className="color-grey">Verbleibend nach Antrag</strong>
							<div className={`title is-4 ${remainingPool < 0 ? 'color-red' : ''}`}>{fmtEUR(remainingPool)}</div>
							<small className="color-grey">von {fmtEUR(availablePool)} verfügbar</small>
						</div>
						<div>
							<div className="progress-wrapper">
								<progress className="progress is-primary" value={usedPct} max="100">
									{Math.round(usedPct)}%
								</progress>{' '}
								<br />
								<small className="color-grey">
									<strong>{Math.round(usedPct)} %</strong> des verfügbaren Budgets verteilt
								</small>
							</div>
						</div>
						<div className="button-group">
							<Button className="button is-inverted" onClick={resetAll} disabled={distributed === 0 && Object.values(allocations).every((v) => v === 0)}>
								Zurücksetzen
							</Button>
							<Button className="button is-primary" onClick={() => setCheckoutOpen(true)} disabled={checkoutItems.length === 0}>
								Antrag prüfen
							</Button>
						</div>
					</div>
				</div>
			</div>

			{/* Quick distribution helpers */}
			<div className="pad-1rem">
				<div className="grid is-col-auto gap-2 align-center">
					<span className="color-grey">
						<strong>Schnellverteilung:</strong>
					</span>
					<Button className="button is-small is-inverted" onClick={distribEvenly}>
						<Icon iconClass="magic" />
						<span>Nach empfehlung</span>
					</Button>
					<Button className="button is-small is-inverted" onClick={distribEvenly}>
						<Icon iconClass="grip-lines" />
						<span>Gleichmäßig verteilen</span>
					</Button>
					<Button className="button is-small is-inverted" onClick={matchLastYear}>
						<Icon iconClass="rotate-left" />
						<span>Auf Vorjahr setzen</span>
					</Button>
					<Button className="button is-small is-inverted" onClick={resetAll}>
						<Icon iconClass="eraser" />
						<span>Zurücksetzen</span>
					</Button>
				</div>
			</div>

			{/* Success message */}
			{successMessage && (
				<div className="pad-1rem">
					<div className="notification is-success is-light">
						<Button className="delete" onClick={() => setSuccessMessage(null)}></Button>
						<Icon iconClass="circle-check" />
						<span style={{ marginLeft: 8 }}>{successMessage}</span>
					</div>
				</div>
			)}

			{/* Table */}
			<div className="agt-list-container border-top">
				<div className="agentur-filter-container is-collapsed">
					<div className="agentur-filter-count">
						{tm.rows.length} / {seedData.length} Agenturen
					</div>
					<div className="current-filters">
						<InputControl
							name="fullText"
							label="Textsuche"
							value={query}
							onChange={(_n: string, v: string) => setQuery(v)}
							debounceTime={250}
							placeholder="Freitext"
							icon="search"
							modifier="current-filter is-inline"
						/>
					</div>
				</div>
			</div>
			<Table tm={tm} stickyHeader={true} stickyFooter={true} />
			{/* Info box */}
			<div className="pad-1rem">
				<div className="note-box is-turquoise bg-turquoise">
					<Icon iconClass="circle-info" />
					<span style={{ marginLeft: 8 }}>
						Höchstbetrag pro Zeile entspricht dem verbleibenden Pool. Negative Werte reduzieren das aktive Guthaben der Agentur (max. bis zur Höhe des aktiven Guthabens). Aufstockungen
						werden erst nach Genehmigung durch das UW-Team wirksam.
					</span>
				</div>
			</div>

			{/* Checkout Modal */}
			{checkoutOpen && (
				<CheckoutModal
					onClose={() => setCheckoutOpen(false)}
					onConfirm={submit}
					items={checkoutItems}
					totals={{
						distributed,
						remaining: remainingPool,
						pool: availablePool,
					}}
				/>
			)}

			{/* History Drawer - placeholder for now */}
			{historyDrawerAgtId && (
				<div className="modal-container">
					<div className="modal-background" onClick={() => setHistoryDrawerAgtId(null)}></div>
					<div className="modal is-medium">
						<div className="modal-header">
							<div className="title">Treuebonus-Historie</div>
							<button className="button is-icon modal-close" onClick={() => setHistoryDrawerAgtId(null)}>
								<Icon iconClass="times" faModifier="fab" />
							</button>
						</div>
						<div className="modal-body">
							<p>Historie für Agentur {historyDrawerAgtId}</p>
							<p className="color-grey">Historische Daten werden hier angezeigt.</p>
						</div>
					</div>
				</div>
			)}
		</div>
	);
});

interface CheckoutModalProps {
	onClose: () => void;
	onConfirm: () => void;
	items: Array<{ agtId: string; bnr: string; name: string; delta: number }>;
	totals: {
		distributed: number;
		remaining: number;
		pool: number;
	};
}

const CheckoutModal: React.FC<CheckoutModalProps> = ({ onClose, onConfirm, items, totals }) => {
	const positives = items.filter((i) => i.delta > 0);
	const negatives = items.filter((i) => i.delta < 0);

	const fmtEUR = (n: number) => formatNumTwoDig(n) + ' €';
	const fmtEURsigned = (n: number) => (n > 0 ? '+' : '') + fmtEUR(n);

	return (
		<div className="modal-container">
			<div className="modal-background" onClick={onClose}></div>
			<div className="modal is-medium">
				<div className="modal-header">
					<div className="title">Antrag prüfen und absenden</div>
					<button className="button is-icon modal-close" onClick={onClose}>
						<Icon iconClass="times" faModifier="fab" />
					</button>
				</div>
				<div className="modal-body">
					<div className="pad-1rem">Bitte prüfen Sie die folgende Verteilung. Nach dem Absenden wird der Antrag an das UW-Team zur Freigabe übermittelt.</div>

					<div className="grid is-col-auto gap-3 pad-1rem border-top border-bottom">
						<div>
							<strong className="color-grey">Verteilte Mittel</strong>
							<div className="title is-4 color-green">{fmtEURsigned(totals.distributed)}</div>
							<small className="color-grey">{items.filter((i) => i.delta !== 0).length} Agenturen</small>
						</div>
						<div>
							<strong className="color-grey">Verbleibend im Pool</strong>
							<div className="title is-4">{fmtEUR(totals.remaining)}</div>
							<small className="color-grey">von {fmtEUR(totals.pool)} Jahresbudget</small>
						</div>
					</div>

					{positives.length > 0 && (
						<div className="pad-top-1rem">
							<h3 className="title is-5">
								<Icon iconClass="arrow-up" faModifier="fas" />
								<span style={{ marginLeft: 6, color: '#1e8927' }}>Aufstockung ({positives.length})</span>
							</h3>
							<table className="table is-fullwidth is-striped">
								<tbody>
									{positives.map((i) => (
										<tr key={i.agtId}>
											<td>
												<strong>{i.name}</strong>
												<div className="color-grey is-size-7">BNR {i.bnr}</div>
											</td>
											<td className="has-text-right" style={{ color: '#1e8927', fontWeight: 600 }}>
												{fmtEURsigned(i.delta)}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}

					{negatives.length > 0 && (
						<div className="pad-top-1rem">
							<h3 className="title is-5">
								<Icon iconClass="arrow-down" faModifier="fas" />
								<span style={{ marginLeft: 6, color: '#e30613' }}>Reduzierung ({negatives.length})</span>
							</h3>
							<table className="table is-fullwidth is-striped">
								<tbody>
									{negatives.map((i) => (
										<tr key={i.agtId}>
											<td>
												<strong>{i.name}</strong>
												<div className="color-grey is-size-7">BNR {i.bnr}</div>
											</td>
											<td className="has-text-right" style={{ color: '#e30613', fontWeight: 600 }}>
												{fmtEURsigned(i.delta)}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}

					<div className="notification is-info is-light pad-top-1rem">
						<Icon iconClass="circle-info" />
						<span style={{ marginLeft: 8 }}>
							Die Bearbeitungszeit durch das UW-Team beträgt in der Regel 3–5 Werktage. Sie erhalten eine Benachrichtigung, sobald über Ihren Antrag entschieden wurde.
						</span>
					</div>
				</div>
				<div className="modal-footer">
					<Button className="button is-inverted" onClick={onClose}>
						Abbrechen
					</Button>
					<Button className="button is-primary" onClick={onConfirm}>
						Antrag absenden
					</Button>
				</div>
			</div>
		</div>
	);
};
